
import React, { useState } from 'react';
import { TreatmentSession, SessionPhoto } from '@/types/treatmentSession';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, parseISO } from 'date-fns';
import { 
  FileImage, 
  Upload, 
  Eye, 
  Download, 
  Trash2 
} from 'lucide-react';

interface SessionPhotosManagerProps {
  session: TreatmentSession;
  onUploadPhoto: (sessionId: number, photo: Omit<SessionPhoto, 'id' | 'sessionId' | 'uploadedAt'>) => Promise<SessionPhoto>;
  onClose: () => void;
}

const SessionPhotosManager: React.FC<SessionPhotosManagerProps> = ({
  session,
  onUploadPhoto,
  onClose
}) => {
  const [photoType, setPhotoType] = useState<'before' | 'after' | 'during'>('before');
  const [photoDescription, setPhotoDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const handleUpload = async () => {
    try {
      setIsUploading(true);
      
      // In a real app, this would upload a real file
      // For now, we'll just use a placeholder
      await onUploadPhoto(session.id, {
        type: photoType,
        fileUrl: "/placeholder.svg",
        description: photoDescription,
        uploadedBy: 'doctor'
      });
      
      // Reset form
      setPhotoDescription('');
    } catch (err) {
      console.error('Error uploading photo:', err);
    } finally {
      setIsUploading(false);
    }
  };
  
  const viewPhoto = (photo: SessionPhoto) => {
    window.open(photo.fileUrl, '_blank');
  };
  
  const downloadPhoto = (photo: SessionPhoto) => {
    // In a real app, this would be a real download
    window.open(photo.fileUrl, '_blank');
  };
  
  const getPhotoTypeLabel = (type: string) => {
    switch (type) {
      case 'before':
        return 'Before Treatment';
      case 'after':
        return 'After Treatment';
      case 'during':
        return 'During Treatment';
      default:
        return 'Treatment Photo';
    }
  };
  
  const beforePhotos = session.photos.filter(photo => photo.type === 'before');
  const afterPhotos = session.photos.filter(photo => photo.type === 'after');
  const duringPhotos = session.photos.filter(photo => photo.type === 'during');
  
  return (
    <div className="py-4">
      <Tabs defaultValue="upload">
        <TabsList className="mb-4">
          <TabsTrigger value="upload">Upload New Photo</TabsTrigger>
          <TabsTrigger value="manage">Manage Existing ({session.photos.length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="photo-type">Photo Type</Label>
              <Select 
                value={photoType} 
                onValueChange={(value) => setPhotoType(value as any)}
              >
                <SelectTrigger id="photo-type">
                  <SelectValue placeholder="Select photo type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">Before Treatment</SelectItem>
                  <SelectItem value="after">After Treatment</SelectItem>
                  <SelectItem value="during">During Treatment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="photo-description">Description (Optional)</Label>
              <Textarea
                id="photo-description"
                value={photoDescription}
                onChange={(e) => setPhotoDescription(e.target.value)}
                placeholder="Enter a description for this photo..."
                className="h-20"
              />
            </div>
            
            <div className="border-2 border-dashed rounded-md p-6 text-center">
              <FileImage className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Drag and drop your image here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Supported formats: JPG, PNG, HEIC
              </p>
              <Button variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Browse Images
              </Button>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Photo'}
              </Button>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="manage">
          <Tabs defaultValue="all">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Photos ({session.photos.length})</TabsTrigger>
              {beforePhotos.length > 0 && <TabsTrigger value="before">Before ({beforePhotos.length})</TabsTrigger>}
              {afterPhotos.length > 0 && <TabsTrigger value="after">After ({afterPhotos.length})</TabsTrigger>}
              {duringPhotos.length > 0 && <TabsTrigger value="during">During ({duringPhotos.length})</TabsTrigger>}
            </TabsList>
            
            <TabsContent value="all">
              <PhotoGrid photos={session.photos} onView={viewPhoto} onDownload={downloadPhoto} />
            </TabsContent>
            
            <TabsContent value="before">
              <PhotoGrid photos={beforePhotos} onView={viewPhoto} onDownload={downloadPhoto} />
            </TabsContent>
            
            <TabsContent value="after">
              <PhotoGrid photos={afterPhotos} onView={viewPhoto} onDownload={downloadPhoto} />
            </TabsContent>
            
            <TabsContent value="during">
              <PhotoGrid photos={duringPhotos} onView={viewPhoto} onDownload={downloadPhoto} />
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface PhotoGridProps {
  photos: SessionPhoto[];
  onView: (photo: SessionPhoto) => void;
  onDownload: (photo: SessionPhoto) => void;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({ photos, onView, onDownload }) => {
  if (photos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No photos uploaded in this category
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {photos.map((photo) => (
        <Card key={photo.id} className="overflow-hidden">
          <CardContent className="p-0">
            <div className="relative">
              <img 
                src={photo.fileUrl} 
                alt={photo.description || "Treatment photo"} 
                className="w-full h-48 object-cover"
              />
              <div className="absolute top-2 right-2">
                <Badge type={photo.type} />
              </div>
            </div>
            
            <div className="p-3">
              <p className="text-sm font-medium">{photo.description || `${photo.type.charAt(0).toUpperCase() + photo.type.slice(1)} Treatment Photo`}</p>
              <p className="text-xs text-muted-foreground">
                Uploaded on {format(parseISO(photo.uploadedAt), 'MMM d, yyyy')}
              </p>
            </div>
            
            <div className="flex justify-end p-2 space-x-2 bg-muted/20">
              <Button size="sm" variant="outline" onClick={() => onView(photo)}>
                <Eye className="h-4 w-4 mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDownload(photo)}>
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Badge: React.FC<{ type: string }> = ({ type }) => {
  let bgColor = '';
  let textColor = '';
  
  switch (type) {
    case 'before':
      bgColor = 'bg-blue-100';
      textColor = 'text-blue-800';
      break;
    case 'after':
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      break;
    case 'during':
      bgColor = 'bg-purple-100';
      textColor = 'text-purple-800';
      break;
    default:
      bgColor = 'bg-gray-100';
      textColor = 'text-gray-800';
  }
  
  return (
    <span className={`${bgColor} ${textColor} px-2 py-1 rounded-md text-xs font-medium`}>
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </span>
  );
};

export default SessionPhotosManager;
